import { User } from './User';
import { Listing, ListingEnquiry } from './Listing';
import { DropzoneFile } from 'components/Dropzone/utils';
import { NdaStatus } from './Nda';
import {
  DataRoomNode,
  DataRoomNodeTag,
} from 'router/subrouters/DataRoom/models/DataRoomNode';
import { ObjectValues } from 'types';
import { DealRoom } from './DealRoom';
import { EnquiryQuestionType } from 'router/subrouters/Dash/subrouters/Business/components/EnquirySlider/utils';
import { NotificationMedium } from './Notification';
import {
  LEAD_NOTIFICATION_TYPE_CODE,
  LeadNotificationStatus,
} from './LeadNotifications';
import { AnalyticsExportTimeframe } from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/types';
import { DataRoomArchiveFileType } from 'router/subrouters/DataRoom/subrouters/Settings/pages/SetupPage/components/Snapshots/types';
import { MEMBER_ACTIVITY_PAGE, MemberActivityPage } from './MixpanelEvents';
import { NdaProvider } from './Agreement';

export type LeadState =
  | 'enquire'
  | 'enquireWaitlisted'
  | 'accept'
  | 'acceptWaitlisted'
  | 'confirm'
  | 'confirmWaitlisted'
  | 'offer'
  | 'offerWaitlisted';

export type WaitlistedStages =
  | 'enquireWaitlisted'
  | 'acceptWaitlisted'
  | 'confirmWaitlisted'
  | 'offerWaitlisted';

export const LEAD_STAGES = {
  ENQUIRE: 'enquire',
  ACCEPT: 'accept',
  CONFIRM: 'confirm',
  OFFER: 'offer',
} as const;
export type LeadStages = ObjectValues<typeof LEAD_STAGES>;

export const LEAD_STAGES_COPY = {
  ENQUIRY: 'enquiry',
  ACCEPTED: 'accepted',
  PROSPECT: 'prospect',
  BIDDER: 'bidder',
} as const;
export type LeadStagesCopy = ObjectValues<typeof LEAD_STAGES_COPY>;

export type Lead = Pick<
  User,
  'firstName' | 'lastName' | 'email' | 'contactNumber'
> & {
  _id: string;

  // states as tags
  withdrawnInterest?: boolean; // Withdrawn
  isExternal?: boolean; // Added
  isPrivateViewer?: boolean; // Invited
  isRequestedPrivateViewer?: boolean; // Requested
  isShareableLinkConsumer?: boolean; // Requested link -> became lead via Secure link
  confirmedInterest?: boolean; // Confirmed
  isOfferRejected?: boolean; // Offer rejected
  isOfferAccepted?: boolean; // Offer accepted
  isInactive?: boolean; // Didn't confirm access
  isReleaseOrderPaused?: boolean;
  isReleaseOrderAwaitingReaction?: boolean;

  isManualWaitlisted?: boolean;
  sessionId: string;
  progressedStep: number;
  buyerId: string;
  sellerId: string;
  leadState: LeadState;
  buyerStep: LeadStages;
  listingEnquiryId: string;
  listingId: string;
  changeLeadStateDate: Date;
  index: number;
  buyer?: Partial<User>;
  listingEnquiry?: Partial<ListingEnquiry> | null;
  seller?: Partial<User> | null;
  listing?: Partial<Listing> | null;
  NBIO: DropzoneFile;
  enquiryQuestions: EnquiryQuestionType[];
  dataRoomMemberId?: string;
  canManageDataRoom?: boolean; // true if user who fetch lead's stat is data room admin or owner
  numberOfAccessableDocuments: number;
  referrerName: string;
  createdAt: Date;
  company?: string;
  linkedinProfile?: string;
  natureOfInterest?: string;
  howDiscoverListing?: string;
  dealRoom?: Partial<DealRoom>;
  hasDataRoomAccess?: boolean;
  brokerViewIds: string[];

  context?: string;

  // the most important X number of data room nodes
  dataRoomTopNodes: DataRoomNodeIdWithStateAndSignal[];
  dataRoomStarredNodes: DataRoomNodeIdWithStateAndSignal[];

  // chat
  lastSeen?: Date;
  unreadMessagesCount?: number;
  unrepliedMessagesCount?: number;

  // notes
  unreadNotesCount?: number;
  unrepliedNotesCount?: number;

  ndaSigningStatus?: NdaStatus;
  ndaFile: DropzoneFile;

  //virtual props
  isDataRoomFile: boolean;
  canMoveFileToDataRoom: boolean;
  dataRoomNodeId: string;

  manuallySetNDAStatus?: boolean;
  manuallySetNDAStatusDate?: Date;
  manuallySetNDAStatusUser?: Partial<User>;

  withdrawnInterestDate?: Date;

  pulseCheckActive?: boolean;
  softSignal?: SoftSignal;
  softSignalAt?: Date;
} & LeadNdaProps;

export type LeadNdaProps = {
  envelopeId?: string; // this is Lead's NDA
  envelopeTemplateId?: string; // NDA exists for that deal
  envelopeTemplateName?: string;
  signingStatus?: NdaStatus;
  sentDate?: Date;
  signedDate?: Date;
  rejectionReason?: string;
  ndaProvider?: NdaProvider;
  agreementExecutionId?: string;
  agreementExecuted?: boolean;
};

export const DATA_ROOM_NODE_ACCESS_STATES = {
  VIEWED: 'viewed',
  NOT_VIEWED: 'not-viewed',
  PENDING: 'pending',
  NO_ACCESS: 'no-access',
} as const;

export type DataRoomNodeAccessState = ObjectValues<
  typeof DATA_ROOM_NODE_ACCESS_STATES
>;

export const DATA_ROOM_NODE_ACCESS_LEVELS = {
  NO_ACCESS: 'no-access',
  VIEW: 'view',
  DOWNLOAD: 'download',
} as const;

export type DataRoomNodeAccessLevel = ObjectValues<
  typeof DATA_ROOM_NODE_ACCESS_LEVELS
>;

export const SOFT_SIGNALS = {
  LIKE: 'like',
  UNSURE: 'unsure',
  DISLIKE: 'dislike',
} as const;

export type SoftSignal = ObjectValues<typeof SOFT_SIGNALS>;

export type SoftSignalPayload = {
  softSignal: SoftSignal;
  message?: string;
  withdrawInterest?: boolean;
};

export type DataRoomNodeWithStateAndSignal =
  DataRoomNodeIdWithStateAndSignal & {
    dataRoomNode: DataRoomNode;
  };

export type DataRoomNodeIdWithStateAndSignal = {
  dataRoomNodeId: string;
  softSignal?: SoftSignal;
  softSignalAt?: Date;

  state: DataRoomNodeAccessState;
  accessLevel?: DataRoomNodeAccessLevel;
  sharedAt?: Date;
};

export const BUYERS_ACTIVITY_TRACK_TYPE = {
  PLATFORM: 'platform',
  NOTIFICATION: 'notification',
  DATA_ROOM: 'data_room',
} as const;

export type BuyersActivityTrackType = ObjectValues<
  typeof BUYERS_ACTIVITY_TRACK_TYPE
>;

export type BuyersActivityTrack = {
  _id: string;

  createdAt: Date;

  type: BuyersActivityTrackType;

  oldStage?: LeadStages & 'withdraw';
  newStage?: LeadStages & 'withdraw';

  softSignal?: SoftSignal;
  dataRoomNodeTag?: DataRoomNodeTag;

  // for workflow automation events
  question?: string;
  answer?: string;
  advancedLead?: boolean;
  withdrawnInterest?: boolean;

  // for sent_nda, resent_nda, signed_nda events
  envelopeTemplateName?: string;

  // lead notification event fields
  leadNotificationTypeCode: number;
  leadNotificationChannel: NotificationMedium;
  leadNotificationStatus: LeadNotificationStatus;

  platformTypeCode?: number;
  dataRoomTypeCode?: number;

  timeSpent?: number;

  userId: string;
  user: Partial<User> | null;

  progressedByUserId: string;
  progressedByUser: Partial<User> | null;

  dealRoomId: string;
  dealRoom: Partial<DealRoom> | null;

  leadId: string;
  lead: Partial<Lead> | null;

  //potentially for file view event
  dataRoomNodeId: string;
  dataRoomNode: Partial<DataRoomNode> | null;

  releasedDataRoomNodeId?: string;
  releasedDataRoomNode?: Partial<DataRoomNode> | null;
  stage?: LeadStages;
  releasedFilesCount?: number;
};

export const BUYERS_ACTIVITY_TRACK_EVENTS = {
  LISTING_ENQUIRED: 'listing_enquired',
  VIEWED_TEASER: 'viewed_teaser',
  SENT_NDA: 'sent_nda',
  RESENT_NDA: 'resent_nda',
  SIGNED_NDA: 'signed_nda',
  VIEWED_FILE: 'viewed_file',
  CONFIRMED_INTEREST: 'confirmed_interest',
  WITHDRAWN_INTEREST: 'withdrawn_interest',
  RELEASED_TAGGED_FILE: 'released_tagged_file',
  VIEWED_TAGGED_FILE: 'viewed_tagged_file',
  MANUALLY_PROGRESSED: 'manually_progressed',
  AUTOMATICALLY_PROGRESSED: 'automatically_progressed',
  FILE_SOFT_SIGNAL: 'file_soft_signal',
  PULSE_CHECK_SENT: 'pulse_check_sent',
  PULSE_CHECK_REACTION: 'pulse_check_reaction',
  SUBMIT_OFFER: 'submit_offer',
  OFFER_ACCEPTED: 'offer_accepted',
  OFFER_REJECTED: 'offer_rejected',

  // Lead notification events
  NOTIFIED: 'notified',

  // Workflow automation events
  CONFIRMED_INTEREST_PROGRESSING_ANSWER:
    'confirmed_interest_progressing_answer',
  CONFIRMED_INTEREST_NON_PROGRESSING_ANSWER:
    'confirmed_interest_non_progressing_answer',
  CONFIRMED_INTEREST_NO_ANSWER_KEEP: 'confirmed_interest_no_answer_keep',
  CONFIRMED_INTEREST_AUTO_ADVANCED: 'confirmed_interest_auto_advanced',
  UNSURE_INTEREST_PROGRESSING_ANSWER: 'unsure_interest_progressing_answer',
  UNSURE_INTEREST_NON_PROGRESSING_ANSWER:
    'unsure_interest_non_progressing_answer',
  UNSURE_INTEREST_NO_ANSWER_KEEP: 'unsure_interest_no_answer_keep',
  UNSURE_INTEREST_AUTO_ADVANCED: 'unsure_interest_auto_advanced',
  PROGRESSION_QUESTION_PROGRESSING: 'progression_question_progressing',
  PROGRESSION_QUESTION_NON_PROGRESSING: 'progression_question_non_progressing',
  PROGRESSION_QUESTION_NO_ANSWER: 'progression_question_no_answer',
  INTEREST_POPUP_NOT_INTERESTED: 'interest_popup_not_interested',
  AUTOMATION_NO_RESPONSE: 'automation_no_response',
  RELEASE_ORDER_MESSAGE_SENT: 'release_order_message_sent',
  RELEASE_ORDER_UNLOCKED: 'release_order_unlocked',
  RELEASE_ORDER_WITHDRAWN_BY_BROKER: 'release_order_withdrawn_by_broker',
  RELEASE_ORDER_REMOVED_FROM_AUTOMATION:
    'release_order_removed_from_automation',
  RELEASE_ORDER_STARTED: 'release_order_started',
  RELEASE_ORDER_INTEREST_SHOWN: 'release_order_interest_shown',
  RELEASE_ORDER_COMPLETED: 'release_order_completed',
  RELEASE_ORDER_PAUSED: 'release_order_paused',
  RELEASE_ORDER_LEAD_WITHDRAWN: 'release_order_lead_withdrawn',
  RELEASE_ORDER_SWITCHED_TO_ALL_AT_ONCE:
    'release_order_switched_to_all_at_once',
  RELEASE_ORDER_ITEM_ADDED: 'release_order_item_added',

  //Data room reports
  LOG_IN: 'log_in',
  LOG_OUT: 'log_out',
  DOCUMENT_VIEWED: 'document_viewed',
  DOCUMENT_DOWNLOADED: 'document_downloaded',
  TIME_SPENT: 'time_spent',
  DRAFTED_QUESTION: 'drafted_question',
  SUBMITTED_QUESTION: 'submitted_question',
} as const;

export type BuyersActivityTrackEvents = ObjectValues<
  typeof BUYERS_ACTIVITY_TRACK_EVENTS
>;

export type BuyersActivityDataRoomEvents =
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.LOG_IN
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.LOG_OUT
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.DOCUMENT_VIEWED
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.DOCUMENT_DOWNLOADED
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.TIME_SPENT
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.DRAFTED_QUESTION
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.SUBMITTED_QUESTION
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_STARTED
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_INTEREST_SHOWN
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_COMPLETED
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_PAUSED
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_LEAD_WITHDRAWN
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_SWITCHED_TO_ALL_AT_ONCE
  | typeof BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_ITEM_ADDED;

export type BuyersActivityNotificationEvents =
  typeof BUYERS_ACTIVITY_TRACK_EVENTS.NOTIFIED;

export const BUYERS_ACTIVITY_PLATFORM_TYPE_CODE: Record<
  Exclude<
    BuyersActivityTrackEvents,
    BuyersActivityNotificationEvents | BuyersActivityDataRoomEvents
  >,
  number
> = {
  [BUYERS_ACTIVITY_TRACK_EVENTS.LISTING_ENQUIRED]: 1,
  [BUYERS_ACTIVITY_TRACK_EVENTS.VIEWED_TEASER]: 2,
  [BUYERS_ACTIVITY_TRACK_EVENTS.SENT_NDA]: 3,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RESENT_NDA]: 4,
  [BUYERS_ACTIVITY_TRACK_EVENTS.SIGNED_NDA]: 5,
  [BUYERS_ACTIVITY_TRACK_EVENTS.VIEWED_FILE]: 6,
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST]: 7,
  [BUYERS_ACTIVITY_TRACK_EVENTS.WITHDRAWN_INTEREST]: 8,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASED_TAGGED_FILE]: 9,
  [BUYERS_ACTIVITY_TRACK_EVENTS.VIEWED_TAGGED_FILE]: 10,
  [BUYERS_ACTIVITY_TRACK_EVENTS.MANUALLY_PROGRESSED]: 11,
  [BUYERS_ACTIVITY_TRACK_EVENTS.AUTOMATICALLY_PROGRESSED]: 12,
  [BUYERS_ACTIVITY_TRACK_EVENTS.FILE_SOFT_SIGNAL]: 13,
  [BUYERS_ACTIVITY_TRACK_EVENTS.PULSE_CHECK_SENT]: 14,
  [BUYERS_ACTIVITY_TRACK_EVENTS.PULSE_CHECK_REACTION]: 15,
  [BUYERS_ACTIVITY_TRACK_EVENTS.SUBMIT_OFFER]: 16,
  [BUYERS_ACTIVITY_TRACK_EVENTS.OFFER_ACCEPTED]: 17,
  [BUYERS_ACTIVITY_TRACK_EVENTS.OFFER_REJECTED]: 18,
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST_PROGRESSING_ANSWER]: 19,
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST_NON_PROGRESSING_ANSWER]: 20,
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST_NO_ANSWER_KEEP]: 21,
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST_AUTO_ADVANCED]: 22,
  [BUYERS_ACTIVITY_TRACK_EVENTS.UNSURE_INTEREST_PROGRESSING_ANSWER]: 23,
  [BUYERS_ACTIVITY_TRACK_EVENTS.UNSURE_INTEREST_NON_PROGRESSING_ANSWER]: 24,
  [BUYERS_ACTIVITY_TRACK_EVENTS.UNSURE_INTEREST_NO_ANSWER_KEEP]: 25,
  [BUYERS_ACTIVITY_TRACK_EVENTS.UNSURE_INTEREST_AUTO_ADVANCED]: 26,
  [BUYERS_ACTIVITY_TRACK_EVENTS.PROGRESSION_QUESTION_PROGRESSING]: 27,
  [BUYERS_ACTIVITY_TRACK_EVENTS.PROGRESSION_QUESTION_NON_PROGRESSING]: 28,
  [BUYERS_ACTIVITY_TRACK_EVENTS.PROGRESSION_QUESTION_NO_ANSWER]: 29,
  [BUYERS_ACTIVITY_TRACK_EVENTS.INTEREST_POPUP_NOT_INTERESTED]: 30,
  [BUYERS_ACTIVITY_TRACK_EVENTS.AUTOMATION_NO_RESPONSE]: 31,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_MESSAGE_SENT]: 32,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_UNLOCKED]: 33,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_WITHDRAWN_BY_BROKER]: 34,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_REMOVED_FROM_AUTOMATION]: 35,
};

export const BUYERS_ACTIVITY_DATA_ROOM_TYPE_CODE: Record<
  BuyersActivityDataRoomEvents,
  number
> = {
  [BUYERS_ACTIVITY_TRACK_EVENTS.LOG_IN]: 1,
  [BUYERS_ACTIVITY_TRACK_EVENTS.LOG_OUT]: 2,
  [BUYERS_ACTIVITY_TRACK_EVENTS.DOCUMENT_VIEWED]: 3,
  [BUYERS_ACTIVITY_TRACK_EVENTS.DOCUMENT_DOWNLOADED]: 4,
  [BUYERS_ACTIVITY_TRACK_EVENTS.TIME_SPENT]: 5,
  [BUYERS_ACTIVITY_TRACK_EVENTS.DRAFTED_QUESTION]: 6,
  [BUYERS_ACTIVITY_TRACK_EVENTS.SUBMITTED_QUESTION]: 7,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_STARTED]: 8,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_INTEREST_SHOWN]: 9,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_COMPLETED]: 10,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_PAUSED]: 11,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_LEAD_WITHDRAWN]: 12,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_SWITCHED_TO_ALL_AT_ONCE]: 13,
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_ITEM_ADDED]: 14,
};

export const LEAD_NOTIFICATION_PLATFORM_TYPE_CODE = {
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_MOVED_TO_ACCEPTED]: 300,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_MOVED_TO_CONFIRMED]: 301,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_MOVED_TO_OFFER]: 302,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_SELLER_ACCEPT_OFFER]: 303,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_SELLER_REJECT_OFFER]: 304,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_LISTING_SOLD]: 305,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_MESSAGE_FROM_SELLER]: 306,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_ACCESSIBLE_DOCUMENTS]: 307,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_NDA_SENT]: 308,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_LEAD_PULSE_CHECK]: 309,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_LEAD_ADDED_TO_DEAL]: 310,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_QUESTION_DRAFTED]: 311,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_QUESTION_REJECTED_BUY_SIDE]: 312,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_QUESTION_REJECTED_SELL_SIDE]: 313,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_QUESTION_REPLIED]: 314,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_QUESTION_EDITED]: 315,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_FILE_ACCESS]: 316,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_CONFIRM_FILES_ACCESS]: 317,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_ENABLED_DATA_ROOM_ACCESS]: 318,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_DOWNLOADED_FILE_EXPIRE_IN_TWO_DAYS]: 319,
  [LEAD_NOTIFICATION_TYPE_CODE.PLATFORM_PENDING_FILES_REMINDER]: 320,
} as const;

export const LEAD_NOTIFICATION_EMAIL_SMS_TYPE_CODE = {
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_NDA_SENT]: 100,
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_NDA_RESENT]: 101,
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_ACCEPTED_INTEREST]: 102,
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_ADDED_TO_DEAL]: 103,
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_NEW_ACCESSIBLE_FILES]: 104,
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_NEW_FAQ]: 105,
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_DATA_ROOM_ACCESS_APPROVED]: 106,
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_DATA_ROOM_ACCESS_DECLINED]: 107,
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_NUDGE]: 108,
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_RFI_SUBMITTED]: 109,
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_UNREAD_MESSAGES]: 110,
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_QUESTION_ANSWERED]: 112,
  [LEAD_NOTIFICATION_TYPE_CODE.EMAIL_PENDING_FILES_REMINDER]: 113,
  [LEAD_NOTIFICATION_TYPE_CODE.SMS_CONFIRM_FILE_INTEREST]: 200,
  [LEAD_NOTIFICATION_TYPE_CODE.SMS_RFI_SUBMITTED]: 201,
} as const;

export const BUYERS_ACTIVITY_TRACK_EVENTS_COPY: Record<
  BuyersActivityTrackEvents,
  string
> = {
  [BUYERS_ACTIVITY_TRACK_EVENTS.LISTING_ENQUIRED]: 'listingEnquired',
  [BUYERS_ACTIVITY_TRACK_EVENTS.VIEWED_TEASER]: 'viewedTeaser',
  [BUYERS_ACTIVITY_TRACK_EVENTS.SENT_NDA]: 'sentNDA',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RESENT_NDA]: 'ndaResent',
  [BUYERS_ACTIVITY_TRACK_EVENTS.SIGNED_NDA]: 'signedNDA',
  [BUYERS_ACTIVITY_TRACK_EVENTS.VIEWED_FILE]: 'viewedFile',
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST]: 'confirmedInterest',
  [BUYERS_ACTIVITY_TRACK_EVENTS.WITHDRAWN_INTEREST]: 'withdrawnInterest',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASED_TAGGED_FILE]: 'releasedTaggedFile',
  [BUYERS_ACTIVITY_TRACK_EVENTS.VIEWED_TAGGED_FILE]: 'viewedTaggedFile',
  [BUYERS_ACTIVITY_TRACK_EVENTS.MANUALLY_PROGRESSED]:
    'leadManuallyProgressedFromTo',
  [BUYERS_ACTIVITY_TRACK_EVENTS.AUTOMATICALLY_PROGRESSED]:
    'leadAutomaticallyProgressedFromTo',
  [BUYERS_ACTIVITY_TRACK_EVENTS.FILE_SOFT_SIGNAL]: 'fileSoftSignal',
  [BUYERS_ACTIVITY_TRACK_EVENTS.PULSE_CHECK_SENT]: 'pulseCheckSent',
  [BUYERS_ACTIVITY_TRACK_EVENTS.PULSE_CHECK_REACTION]: 'pulseCheckReaction',
  [BUYERS_ACTIVITY_TRACK_EVENTS.SUBMIT_OFFER]: 'offerSubmitted',
  [BUYERS_ACTIVITY_TRACK_EVENTS.OFFER_ACCEPTED]: 'offerAccepted',
  [BUYERS_ACTIVITY_TRACK_EVENTS.OFFER_REJECTED]: 'offerRejected',
  [BUYERS_ACTIVITY_TRACK_EVENTS.NOTIFIED]: 'leadNotified',

  // for workflow automation events
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST_PROGRESSING_ANSWER]:
    'leadConfirmedInterestProgressingAnswer',
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST_NON_PROGRESSING_ANSWER]:
    'leadConfirmedInterestNonProgressingAnswer',
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST_NO_ANSWER_KEEP]:
    'leadConfirmedInterestNoAnswer',
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST_AUTO_ADVANCED]:
    'leadConfirmedInterestAutoAdvanced',
  [BUYERS_ACTIVITY_TRACK_EVENTS.UNSURE_INTEREST_PROGRESSING_ANSWER]:
    'leadUnsureProgressingAnswer',
  [BUYERS_ACTIVITY_TRACK_EVENTS.UNSURE_INTEREST_NON_PROGRESSING_ANSWER]:
    'leadUnsureNonProgressingAnswer',
  [BUYERS_ACTIVITY_TRACK_EVENTS.UNSURE_INTEREST_NO_ANSWER_KEEP]:
    'leadUnsureNoProgression',
  [BUYERS_ACTIVITY_TRACK_EVENTS.UNSURE_INTEREST_AUTO_ADVANCED]:
    'leadUnsureInterestAutoAdvanced',
  [BUYERS_ACTIVITY_TRACK_EVENTS.PROGRESSION_QUESTION_PROGRESSING]:
    'leadProgressionQuestionOnlyProgressingAnswer',
  [BUYERS_ACTIVITY_TRACK_EVENTS.PROGRESSION_QUESTION_NON_PROGRESSING]:
    'leadProgressionQuestionOnlyNonProgressingAnswer',
  [BUYERS_ACTIVITY_TRACK_EVENTS.PROGRESSION_QUESTION_NO_ANSWER]:
    'leadProgressionQuestionOnlyNoAnswer',
  [BUYERS_ACTIVITY_TRACK_EVENTS.INTEREST_POPUP_NOT_INTERESTED]:
    'leadNotInterestedWithdrew',
  [BUYERS_ACTIVITY_TRACK_EVENTS.AUTOMATION_NO_RESPONSE]:
    'leadAutomationNoResponse',

  [BUYERS_ACTIVITY_TRACK_EVENTS.LOG_IN]: 'loggedIn',
  [BUYERS_ACTIVITY_TRACK_EVENTS.LOG_OUT]: 'loggedOut',
  [BUYERS_ACTIVITY_TRACK_EVENTS.DOCUMENT_VIEWED]: 'openedDocument',
  [BUYERS_ACTIVITY_TRACK_EVENTS.DOCUMENT_DOWNLOADED]: 'downloadedDocument',
  [BUYERS_ACTIVITY_TRACK_EVENTS.TIME_SPENT]: 'spentTimeOnPlatform',
  [BUYERS_ACTIVITY_TRACK_EVENTS.DRAFTED_QUESTION]: 'draftedQuestion',
  [BUYERS_ACTIVITY_TRACK_EVENTS.SUBMITTED_QUESTION]: 'submittedQuestion',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_MESSAGE_SENT]:
    'releaseOrderMessageSent',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_UNLOCKED]: 'releaseOrderUnlocked',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_WITHDRAWN_BY_BROKER]:
    'releaseOrderWithdrawnByBroker',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_REMOVED_FROM_AUTOMATION]:
    'releaseOrderRemovedFromAutomation',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_STARTED]: 'releaseOrderStarted',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_INTEREST_SHOWN]:
    'releaseOrderInterestShown',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_COMPLETED]:
    'releaseOrderCompleted',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_PAUSED]: 'releaseOrderPaused',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_LEAD_WITHDRAWN]:
    'releaseOrderLeadWithdrawn',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_SWITCHED_TO_ALL_AT_ONCE]:
    'releaseOrderSwitchedToAllAtOnce',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_ITEM_ADDED]:
    'releaseOrderItemAdded',
};

// used for Activity metrics table
export const BUYERS_ACTIVITY_TRACK_EVENTS_COMPACT_COPY: Record<
  BuyersActivityTrackEvents,
  string
> = {
  [BUYERS_ACTIVITY_TRACK_EVENTS.LISTING_ENQUIRED]: 'listingEnquired',
  [BUYERS_ACTIVITY_TRACK_EVENTS.VIEWED_TEASER]: 'viewedTeaser',
  [BUYERS_ACTIVITY_TRACK_EVENTS.SENT_NDA]: 'sentNDA',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RESENT_NDA]: 'ndaResent',
  [BUYERS_ACTIVITY_TRACK_EVENTS.SIGNED_NDA]: 'signedNDA',
  [BUYERS_ACTIVITY_TRACK_EVENTS.VIEWED_FILE]: 'viewedFile',
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST]: 'confirmedInterest',
  [BUYERS_ACTIVITY_TRACK_EVENTS.WITHDRAWN_INTEREST]: 'withdrawnInterest',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASED_TAGGED_FILE]: 'taggedFileReleased',
  [BUYERS_ACTIVITY_TRACK_EVENTS.VIEWED_TAGGED_FILE]: 'viewedTaggedFileExactly',
  [BUYERS_ACTIVITY_TRACK_EVENTS.MANUALLY_PROGRESSED]: 'leadManuallyProgressed',
  [BUYERS_ACTIVITY_TRACK_EVENTS.AUTOMATICALLY_PROGRESSED]:
    'leadAutomaticallyProgressed',
  [BUYERS_ACTIVITY_TRACK_EVENTS.FILE_SOFT_SIGNAL]: 'showedInterestInFile',
  [BUYERS_ACTIVITY_TRACK_EVENTS.PULSE_CHECK_SENT]: 'pulseCheckSent',
  [BUYERS_ACTIVITY_TRACK_EVENTS.PULSE_CHECK_REACTION]: 'reactedToPulseCheck',
  [BUYERS_ACTIVITY_TRACK_EVENTS.SUBMIT_OFFER]: 'offerSubmitted',
  [BUYERS_ACTIVITY_TRACK_EVENTS.OFFER_ACCEPTED]: 'offerAccepted',
  [BUYERS_ACTIVITY_TRACK_EVENTS.OFFER_REJECTED]: 'offerRejected',
  [BUYERS_ACTIVITY_TRACK_EVENTS.NOTIFIED]: '',

  // for workflow automation events
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST_PROGRESSING_ANSWER]:
    'leadConfirmedInterestProgressingAnswerCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST_NON_PROGRESSING_ANSWER]:
    'leadConfirmedInterestNonProgressingAnswerCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST_NO_ANSWER_KEEP]:
    'leadConfirmedInterestNoAnswerCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.CONFIRMED_INTEREST_AUTO_ADVANCED]:
    'leadConfirmedInterestAutoAdvancedCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.UNSURE_INTEREST_PROGRESSING_ANSWER]:
    'leadUnsureProgressingAnswerCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.UNSURE_INTEREST_NON_PROGRESSING_ANSWER]:
    'leadUnsureNonProgressingAnswerCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.UNSURE_INTEREST_NO_ANSWER_KEEP]:
    'leadUnsureNoProgressionCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.UNSURE_INTEREST_AUTO_ADVANCED]:
    'leadUnsureInterestAutoAdvancedCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.PROGRESSION_QUESTION_PROGRESSING]:
    'leadProgressionQuestionOnlyProgressingAnswerCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.PROGRESSION_QUESTION_NON_PROGRESSING]:
    'leadProgressionQuestionOnlyNonProgressingAnswerCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.PROGRESSION_QUESTION_NO_ANSWER]:
    'leadProgressionQuestionOnlyNoAnswerCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.INTEREST_POPUP_NOT_INTERESTED]:
    'leadNotInterestedWithdrewCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.AUTOMATION_NO_RESPONSE]:
    'leadAutomationNoResponseCompact',

  [BUYERS_ACTIVITY_TRACK_EVENTS.LOG_IN]: 'loggedIn',
  [BUYERS_ACTIVITY_TRACK_EVENTS.LOG_OUT]: 'loggedOut',
  [BUYERS_ACTIVITY_TRACK_EVENTS.DOCUMENT_VIEWED]: 'documentOpened',
  [BUYERS_ACTIVITY_TRACK_EVENTS.DOCUMENT_DOWNLOADED]: 'documentDownloaded',
  [BUYERS_ACTIVITY_TRACK_EVENTS.TIME_SPENT]: 'timeSpentOnPlatform',
  [BUYERS_ACTIVITY_TRACK_EVENTS.DRAFTED_QUESTION]: 'draftedQuestion',
  [BUYERS_ACTIVITY_TRACK_EVENTS.SUBMITTED_QUESTION]: 'submittedQuestion',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_MESSAGE_SENT]:
    'releaseOrderMessageSentCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_UNLOCKED]:
    'releaseOrderUnlockedCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_WITHDRAWN_BY_BROKER]:
    'releaseOrderWithdrawnByBrokerCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_REMOVED_FROM_AUTOMATION]:
    'releaseOrderRemovedFromAutomationCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_STARTED]:
    'releaseOrderStartedCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_INTEREST_SHOWN]:
    'releaseOrderInterestShownCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_COMPLETED]:
    'releaseOrderCompletedCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_PAUSED]:
    'releaseOrderPausedCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_LEAD_WITHDRAWN]:
    'releaseOrderLeadWithdrawnCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_SWITCHED_TO_ALL_AT_ONCE]:
    'releaseOrderSwitchedToAllAtOnceCompact',
  [BUYERS_ACTIVITY_TRACK_EVENTS.RELEASE_ORDER_ITEM_ADDED]:
    'releaseOrderItemAddedCompact',
};

export const MEMBER_ACTIVITY_EXPORT_PAGE = {
  LEAD_BOARD: 'lead_board',
  MEMBERS_PAGE: 'members_page',
} as const;

export type MemberActivityExportPage = ObjectValues<
  typeof MEMBER_ACTIVITY_EXPORT_PAGE
>;

export const MEMBER_ACTIVITY_PAGE_TO_EXPORT_PAGE: Record<
  MemberActivityPage,
  MemberActivityExportPage
> = {
  [MEMBER_ACTIVITY_PAGE.LEAD_BOARD]: MEMBER_ACTIVITY_EXPORT_PAGE.LEAD_BOARD,
  [MEMBER_ACTIVITY_PAGE.MEMBERS_PAGE]: MEMBER_ACTIVITY_EXPORT_PAGE.MEMBERS_PAGE,
};

export type ExportMemberActivityValues = {
  timeframe: AnalyticsExportTimeframe;
  format: DataRoomArchiveFileType;
  sections: BuyersActivityTrackType[];
  page: MemberActivityExportPage;
};

export type MemberActivityExportParams = ExportMemberActivityValues & {
  uniqueId: string;
};
